if (!customElements.get('agoga-header')) {
  customElements.define('agoga-header', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.menu = this.querySelector('.agoga-mobile-menu');
      this.panel = this.querySelector('.agoga-mobile-menu__panel');
      this.menuToggle = this.querySelector('.agoga-header__menu-toggle');
      this.headerWrapper = this.closest('.section-header');
      this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdownCloseTimer = null;
      this.lastFocusedElement = null;

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.handleResize.bind(this);

      this.menuToggle?.addEventListener('click', () => this.openMenu());
      this.querySelector('.agoga-mobile-menu__close')
        ?.addEventListener('click', () => this.closeMenu());
      this.querySelector('.agoga-mobile-menu__backdrop')
        ?.addEventListener('click', () => this.closeMenu());

      this.querySelectorAll('.agoga-mobile-menu__accordion').forEach((button) => {
        button.addEventListener('click', () => this.toggleMobileAccordion(button));
      });

      this.querySelectorAll('[data-nav-item]').forEach((item) => {
        const button = item.querySelector('[data-dropdown-toggle]');
        if (!button) return;

        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const isOpen = button.getAttribute('aria-expanded') === 'true';
          isOpen ? this.closeDropdown(button) : this.openDropdown(button);
        });

        button.addEventListener('focus', () => {
          if (this.hoverCapable.matches) this.openDropdown(button);
        });

        item.addEventListener('pointerenter', () => {
          if (!this.hoverCapable.matches) return;
          window.clearTimeout(this.dropdownCloseTimer);
          this.openDropdown(button);
        });

        item.addEventListener('pointerleave', () => {
          if (!this.hoverCapable.matches) return;
          window.clearTimeout(this.dropdownCloseTimer);
          this.dropdownCloseTimer = window.setTimeout(() => this.closeDropdown(button), 150);
        });
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.handleScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.dropdownCloseTimer);

      document.body.classList.remove('agoga-menu-open');
      document.body.style.overflow = '';
      this.headerWrapper?.classList.remove('is-agoga-scrolled');
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.closeDropdowns();
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        if (this.menu && !this.menu.hidden) {
          this.closeMenu();
        } else {
          this.closeDropdowns();
        }
      }

      if (event.key === 'Tab' && this.menu && !this.menu.hidden) {
        this.trapMenuFocus(event);
      }
    }

    handleScroll() {
      if (this.dataset.transparent !== 'true') return;
      const isScrolled = window.scrollY > 54;
      this.classList.toggle('is-scrolled', isScrolled);
      this.headerWrapper?.classList.toggle('is-agoga-scrolled', isScrolled);
      if (isScrolled) this.closeDropdowns();
    }

    handleResize() {
      if (window.innerWidth > 1180 && this.menu && !this.menu.hidden) this.closeMenu();
      this.closeDropdowns();
    }

    toggleMobileAccordion(button) {
      const panel = button.nextElementSibling;
      const isOpen = button.getAttribute('aria-expanded') === 'true';

      this.querySelectorAll('.agoga-mobile-menu__accordion').forEach((otherButton) => {
        if (otherButton === button) return;
        otherButton.setAttribute('aria-expanded', 'false');
        if (otherButton.nextElementSibling) otherButton.nextElementSibling.hidden = true;
      });

      button.setAttribute('aria-expanded', String(!isOpen));
      if (panel) panel.hidden = isOpen;
    }

    openMenu() {
      if (!this.menu) return;

      this.lastFocusedElement = document.activeElement;
      this.closeDropdowns();
      this.menu.hidden = false;
      document.body.classList.add('agoga-menu-open');
      document.body.style.overflow = 'hidden';
      this.menuToggle?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.panel?.focus({ preventScroll: true });
      });
    }

    closeMenu() {
      if (!this.menu || this.menu.hidden) return;

      this.menu.hidden = true;
      document.body.classList.remove('agoga-menu-open');
      document.body.style.overflow = '';
      this.menuToggle?.setAttribute('aria-expanded', 'false');

      this.querySelectorAll('.agoga-mobile-menu__accordion').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        if (button.nextElementSibling) button.nextElementSibling.hidden = true;
      });

      if (this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    trapMenuFocus(event) {
      const focusable = [...this.menu.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openDropdown(button) {
      if (!button) return;
      window.clearTimeout(this.dropdownCloseTimer);
      this.closeDropdowns(button);

      const panel = button.nextElementSibling;
      button.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
    }

    closeDropdown(button) {
      if (!button) return;
      button.setAttribute('aria-expanded', 'false');
      if (button.nextElementSibling) button.nextElementSibling.hidden = true;
    }

    closeDropdowns(except) {
      this.querySelectorAll('[data-dropdown-toggle]').forEach((button) => {
        if (button === except) return;
        this.closeDropdown(button);
      });
    }
  });
}
